import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { DeviationMembers } from "@pages/Deviations/Members/DeviationMembers";
import { t } from "i18next";
import { useState } from "react";
import { DataObjectFieldInput } from "../../../../../components/deprecated/DataObjectFieldInput/DataObjectFieldInput";
import { Label } from "../../../../../components/deprecated/Label/Label";
import { presentPopupWithTimeout, removePopUp } from "../../../../../components/deprecated/Popup/deprecated_PopUps";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { RightFullHeightPopUp } from "../../../../../components/deprecated/RightFullHeight/RightFullHeightPopUp";
import { ActionValidator } from "../../../../../auth/actionValidator";
import { DeviationData } from "../../../Deviation/DeviationData";
import { DeviationUpdateData } from "../../../Deviation/DeviationUpdateData";
import { PropertyData } from "../../../Property/PropertyData";
import { UserData } from "../../../User/UserData";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { DataObjectHelpers } from "../../DataObjects/DataObjectHelpers";
import { LocalData } from "../../LocalData";

type DeviationUserSelectorsProps = {
  propertyID: string;
  units: string[];
  contractorGroups: string[];

  defaultSelected: UserData[];
  onSelect: (users: UserData[]) => void;
  title: string;
  buttonTitle: string;
};

const DeviationUserSelectors = ({
  propertyID,
  units,
  contractorGroups,
  defaultSelected,
  onSelect,
  title,
  buttonTitle,
}: DeviationUserSelectorsProps) => {
  const [selectedUsers, setSelectedUsers] = useState<UserData[]>(defaultSelected);

  const property = LocalData.properties.addData(propertyID);
  return (
    <>
      <Label text={title} type="header-2" />
      <DeviationMembers
        property={property}
        contractorGroups={contractorGroups.map((id) => LocalData.contractorGroups.addData(id))}
        units={units.map((id) => property.getUnitWithId(id))}
        setSelectedMembers={(members) => {
          setSelectedUsers(members.map((member) => LocalData.users.addData(member.id)));
        }}
        selectedMembers={selectedUsers.map((member) => property.members.addData(member.id))}
      />
      <Button onClick={() => onSelect(selectedUsers)}>{buttonTitle}</Button>
    </>
  );
};

export class DeviationDataActions {
  static presentDeviationSetting<T>(
    deviation: DeviationData,
    dataField: DataObjectField<T, DeviationData>,
    updateField: DataObjectField<T, DeviationUpdateData>,
    setData: (data: any) => void
  ) {
    Popup.present((id) => (
      <DataObjectFieldInput
        id={id}
        field={updateField}
        defaultValue={dataField.dataGetter(deviation)}
        context={{ propertyID: deviation.parent?.id, menuPortalTarget: "popup" }}
        onFinish={(value) => {
          setData(value);
        }}
        description={t("main.tooltip_delayForUpdates")}
      />
    ));
  }

  static getPossibleFieldsToEdit(deviation: DeviationData): [DataObjectField<any, DeviationData>, DataObjectField<any, DeviationUpdateData>][] {
    const fields: [DataObjectField<any, DeviationData>, DataObjectField<any, DeviationUpdateData>][] = [];
    const property = deviation.parent instanceof PropertyData ? deviation.parent : undefined;
    if (!property) return fields;

    // Status
    if (
      ActionValidator.isAllowedToPerformAction("deviation_change_status", [property]) ||
      deviation.executors.includes(LocalData.currentUser) ||
      deviation.responsibles.includes(LocalData.currentUser)
    ) {
      fields.push([DataObjectHelpers.deviation.status, DataObjectHelpers.deviationUpdate.status]);
    }

    if (ActionValidator.isAllowedToPerformAction("deviation_edit_general_settings", [property])) {
      fields.push(
        [DataObjectHelpers.deviation.title, DataObjectHelpers.deviationUpdate.newTitle],
        [DataObjectHelpers.deviation.description, DataObjectHelpers.deviationUpdate.newDescription]
      );
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_edit_floors", [property])) {
      fields.push([DataObjectHelpers.deviation.floors, DataObjectHelpers.deviationUpdate.floors]);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_edit_place", [property])) {
      fields.push([DataObjectHelpers.deviation.placeDescription, DataObjectHelpers.deviationUpdate.placeDescription]);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_edit_visibility", [property])) {
      fields.push([DataObjectHelpers.deviation.visibility, DataObjectHelpers.deviationUpdate.updatedVisibility]);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_edit_start", [property])) {
      fields.push([DataObjectHelpers.deviation.startDate, DataObjectHelpers.deviationUpdate.startDate]);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_edit_deadline", [property])) {
      fields.push([DataObjectHelpers.deviation.endDate, DataObjectHelpers.deviationUpdate.deadline]);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_change_priority", [property])) {
      fields.push([DataObjectHelpers.deviation.priority, DataObjectHelpers.deviationUpdate.priority]);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_change_invoiceReceiver", [property])) {
      fields.push([DataObjectHelpers.deviation.invoicedTo, DataObjectHelpers.deviationUpdate.invoicedTo]);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_assign_contractors", [property])) {
      //fields.push(DataObjectHelpers.deviation.contractorGroups);
    }
    if (ActionValidator.isAllowedToPerformAction("deviation_assign_responsibles_executors", [property])) {
      //fields.push(DataObjectHelpers.deviation.responsibles);
      //fields.push(DataObjectHelpers.deviation.executors);
    }

    return fields;
  }

  static editResponsible(
    responsibles: string[],
    contractorGroups: string[],
    propertyID: string,
    units: string[],
    onFinish: (users: UserData[]) => void
  ) {
    presentPopupWithTimeout(
      <RightFullHeightPopUp>
        <DeviationUserSelectors
          defaultSelected={responsibles.map((r) => LocalData.users.addData(r))}
          contractorGroups={contractorGroups}
          propertyID={propertyID}
          units={units}
          onSelect={(users) => {
            removePopUp();
            onFinish(users);
          }}
          title={t("main.responsibles")}
          buttonTitle={t("main.changeResponsibles")}
        />
      </RightFullHeightPopUp>
    );
  }

  static editExecutors(executors: string[], contractorGroups: string[], propertyID: string, units: string[], onFinish: (users: UserData[]) => void) {
    presentPopupWithTimeout(
      <RightFullHeightPopUp>
        <DeviationUserSelectors
          defaultSelected={executors.map((r) => LocalData.users.addData(r))}
          contractorGroups={contractorGroups}
          propertyID={propertyID}
          units={units}
          onSelect={(users) => {
            removePopUp();
            onFinish(users);
          }}
          title={t("main.executors")}
          buttonTitle={t("main.changeExecutors")}
        />
      </RightFullHeightPopUp>
    );
  }
}
